import {BUILD_TIMESTAMP, BUILD_VERSION, GATE_COVER_PANEL_POSITION, GATE_TYPE, PANEL_TYPE, BLINK_SDK_READY_EVENT, BLINK_ACTION_URL_PARAM, BLINK_SDK_SETUP_EVENT, type SdkUserData} from "./Constants";
import {evaluatePanelOptions, type PanelOptions, type PanelPromptOptions} from "./PanelOptionsLogic";
import {IFrameMessages} from "./messaging/IFrameMessages";
import {formatAgoMessage, getClientIdFromCurrentScriptUrl, loadBlinkOAuthParams, type OAuthCallbackArgs} from "./utils/Utils";
import {generateUniqueId, getQueryParam, isPlainObject, NOOP_FUNCTION} from "../blinkpay/UtilsLib";
import {dispatchAnalyticsEvent, AnalyticsEventType, type AnalyticsEvent} from "./utils/AnalyticsClient";
import {userDataModule} from "./modules/UserDataModule";
import {iframeToSdkChannel} from "./messaging/IframeToSdkChannel";
import {uxFlowModule} from "./modules/UXFlowModule";
import {purchaseModule, type PaymentRequest} from "./modules/PurchaseModule";
import {analyticsModule} from "./modules/AnalyticsModule";
import {sdkState} from "./modules/SDKState";
import {panelModule} from "./modules/PanelModule";
import {walletModule} from "./modules/WalletModule";
import {userJourneyModule, type JourneyEntrypoint} from "./modules/user-journey/UserJourneyModule";
import {merchantFunctionModule} from "./modules/merchant-state/MerchantFunctionModule";
import {type MerchantFunction} from "./modules/merchant-state/Builtins";
import {merchantVariableModule} from "./modules/merchant-state/MerchantVariableModule";
import {StorageScope} from "./modules/merchant-state/MerchantStorage";
import {fetchPublicData, publicDataLoaded, getDefaultSDKOptions, getPublicDataStore, getMerchantId} from "./modules/PublicData";
import {getPageLoadTimings, markPageLoadTiming, PAGE_LOAD_TIMING_MARKS} from "./utils/PerformanceTiming.js";
import {widgetLoadedDispatcher, type BuildVersion} from "./messaging/SdkToIFrameChannel";
import {runBlinkAction} from "./modules/user-journey/engine/JourneyActionRunner";
import {initMerchantBuiltinFunctions} from "./modules/merchant-state/initMerchantBuiltinFunctions.js";
import {markURLVisited, setHashRoutingEnabled} from "./utils/PageAccessHelpers.js";
import {evaluateConditionAndRun} from "./modules/user-journey/engine/JourneyConditionEvaluator.js";
import type {JourneyOptions} from "./modules/user-journey/engine/Condition";

// TODO @cleanup there should be fewer types of Record in this file, but rather strict types
function normalizePanelOptions(optionsOrPanelId: string | PanelPromptOptions) {
    if (!optionsOrPanelId) {
        return {};
    }
    if (!isPlainObject(optionsOrPanelId)) {
        return {panel: optionsOrPanelId};
    }

    // For backwards compatibility, allow multiple keys
    optionsOrPanelId.panel = optionsOrPanelId.panel || optionsOrPanelId.pgId || optionsOrPanelId.panelId || optionsOrPanelId.panelAlias;

    return optionsOrPanelId;
}

let widgetVersion: Record<string, any> | null = null;
const sdkVersion = {
    build: BUILD_VERSION,
    time: new Date(BUILD_TIMESTAMP),
};
let isInitCalled = false;
let isInitFinished = false;

interface WidgetPositionYOffset {
    desktop?: number;
    mobile?: number;
}

interface SDKInitOptions {
    clientId?: string;
    publisherDomainId?: string;
    storeAnalyticsCookies?: boolean;
    allowUserToCoverFees?: boolean;
    variables?: Record<string, any>;
    hideWallet?: boolean;
    hashRouting?: boolean;
    widgetPositionYOffset?: WidgetPositionYOffset;
    journeys?: JourneyEntrypoint[];
    [key: string]: any;
}

let initOptions: SDKInitOptions = null;

// What a merchant hands one of the prompt methods below: which panel to open, plus any panel option to
// override it with. The overrides are every field of PanelOptions, whose comments are their documentation
interface AuthPanelOptions extends PanelPromptOptions {
    oauthCallbackArgs?: true | OAuthCallbackArgs;
}

class BlinkSDK {
    constructor() {
        markPageLoadTiming(PAGE_LOAD_TIMING_MARKS.sdkJS);

        initMerchantBuiltinFunctions();

        analyticsModule.init();
        userDataModule.init();
        purchaseModule.init();
        walletModule.init();
        panelModule.init();
        uxFlowModule.init();

        iframeToSdkChannel.addListener(IFrameMessages.WIDGET_DATA_LOADED, () => {
            dispatchAnalyticsEvent(AnalyticsEventType.PAGE_VIEW, getPageLoadTimings());

            window.dispatchEvent(new CustomEvent(BLINK_SDK_SETUP_EVENT, {}));

            if (initOptions.journeys) {
                userJourneyModule.init(initOptions.journeys);
            }
            panelModule.resolvePendingRequests();
            purchaseModule.resolvePendingRequests();

            runBlinkAction(getQueryParam(BLINK_ACTION_URL_PARAM));

            window.dispatchEvent(new CustomEvent(BLINK_SDK_READY_EVENT, {}));

            isInitFinished = true;
        });

        const clientId = getClientIdFromCurrentScriptUrl();
        if (clientId) {
            this.init({clientId});
        }
    }

    doInit(givenOptions: SDKInitOptions) {
        initOptions = {
            // Including here all options with a non-null default.
            storeAnalyticsCookies: true,
            ...getDefaultSDKOptions(),
            ...givenOptions,
        };

        // TODO @flow why is this a special case?
        if (initOptions.allowUserToCoverFees) {
            sdkState.update({allowUserToCoverFees: initOptions.allowUserToCoverFees});
        }

        if (initOptions.variables) {
            merchantVariableModule.add(initOptions.variables);
        }

        if (initOptions.hashRouting) {
            setHashRoutingEnabled(true);
        }

        analyticsModule.setEnableStorage(!!initOptions.storeAnalyticsCookies);

        sdkState.update({
            hideWallet: !!initOptions.hideWallet,
        });
        walletModule.renderWallet(initOptions.widgetPositionYOffset);

        // TODO @branch Remove this.
        window.dispatchEvent(new CustomEvent("blinkPaywallInitialized", {}));

        markPageLoadTiming(PAGE_LOAD_TIMING_MARKS.sdkInit);
    }

    // PUBLIC API STARTS HERE

    // Useful constants for the createPanel function
    PANEL_TYPE = PANEL_TYPE;
    GATE_TYPE = GATE_TYPE;
    GATE_COVER_PANEL_POSITION = GATE_COVER_PANEL_POSITION;

    async init(options: SDKInitOptions): Promise<void> {
        if (isInitCalled) {
            return;
        }
        isInitCalled = true;

        // This is required in order to be backwards compatible with the deprecated setOptions() API
        // TODO: Keep an array of deprecated options (and their renaming counterparts).
        fetchPublicData(options.clientId || options.publisherDomainId);

        widgetLoadedDispatcher.addListener((version: BuildVersion) => {
            // TODO @branch this logic probably belongs right next to where we first call widgetLoadedDispatcher
            widgetVersion = version;
            markPageLoadTiming(PAGE_LOAD_TIMING_MARKS.iFrameJS);
        });

        await Promise.all([widgetLoadedDispatcher.awaitOnce(), publicDataLoaded.awaitOnce()]);
        this.doInit(options);
    }

    // Is true once everything is loaded (journeys are run, data is loaded, etc).
    isInitialized(): boolean {
        return isInitFinished;
    }

    setOptions(options: Partial<SDKInitOptions>): void {
        if (!isInitCalled) {
            this.init(options);
        } else {
            sdkState.update(options);
        }
    }

    requestPayment(request: PaymentRequest, callback = NOOP_FUNCTION, errorCallback = NOOP_FUNCTION): void {
        purchaseModule.requestPayment(request, callback, errorCallback);
    }

    createPanel(nodesOrSelector: Element | string | null, optionsOrPanelId: string | PanelPromptOptions): void {
        panelModule.createPanel(nodesOrSelector, normalizePanelOptions(optionsOrPanelId));
    }

    createPopup(optionsOrPanelId: string | PanelPromptOptions): void {
        // TODO @cleanup I think we should just say:
        //  const panelOptions = normalizePanelOptions(...args);
        //  panelModule.createPopup(panelOptions);
        panelModule.createPopup(normalizePanelOptions(optionsOrPanelId));
    }

    // Flows
    promptAuth(optionsOrPanelId: string | AuthPanelOptions) {
        const options: AuthPanelOptions = normalizePanelOptions(optionsOrPanelId);
        if (options.oauthCallbackArgs == true) {
            options.oauthCallbackArgs = loadBlinkOAuthParams();
        }
        uxFlowModule.enterFlow(PANEL_TYPE.auth, options);
    }

    promptSubscription(optionsOrPanelId: string | PanelPromptOptions): void {
        uxFlowModule.enterFlow(PANEL_TYPE.subscribe, normalizePanelOptions(optionsOrPanelId));
    }

    promptGiftSubscription(optionsOrPanelId: string | PanelPromptOptions): void {
        uxFlowModule.enterFlow(PANEL_TYPE.giftSubscription, normalizePanelOptions(optionsOrPanelId));
    }

    promptDonation(optionsOrPanelId: string | PanelPromptOptions): void {
        uxFlowModule.enterFlow(PANEL_TYPE.donation, normalizePanelOptions(optionsOrPanelId));
    }

    promptShop(optionsOrPanelId: string | PanelPromptOptions): void {
        uxFlowModule.enterFlow(PANEL_TYPE.shop, normalizePanelOptions(optionsOrPanelId));
    }

    promptEmailNewsletter(optionsOrPanelId: string | PanelPromptOptions): void {
        uxFlowModule.enterFlow(PANEL_TYPE.newsletter, normalizePanelOptions(optionsOrPanelId));
    }

    promptManageAccount(optionsOrPanelId: string | PanelPromptOptions): void {
        this.createPopup({...normalizePanelOptions(optionsOrPanelId), type: PANEL_TYPE.dashboard});
    }

    // Authentication and user data

    getUserData(): SdkUserData {
        return userDataModule.userData;
    }

    isAuthenticated(): boolean {
        return userDataModule.isAuthenticated();
    }

    isSubscribed(): boolean {
        return userDataModule.isSubscriber();
    }

    getSubscription() {
        return userDataModule.userData.subscription;
    }

    // TODO @cleanup what is this bullshit name?
    getStore(objectType: string): any[] {
        return getPublicDataStore(objectType);
    }

    onAuthenticationChange(callback: (data: { authenticated: boolean }) => void, runAtStart = false): void {
        // TODO: hack
        const callbackWrapper = () => callback({authenticated: this.isAuthenticated()});
        userDataModule.authenticationChange.addListener(callbackWrapper);
        if (runAtStart) {
            if (isInitFinished) {
                callbackWrapper();
            } else {
                const handler = () => {
                    callbackWrapper();
                    window.removeEventListener(BLINK_SDK_READY_EVENT, handler);
                };
                window.addEventListener(BLINK_SDK_READY_EVENT, handler);
            }
        }
    }

    onSubscriptionChange(callback: (subscription: any) => void): void {
        userDataModule.subscriptionChange.addListener(() => callback(userDataModule.userData.subscription));
    }

    // --- Short hand functions for updating options
    setAnalyticsStorageEnabled(value: boolean): void {
        analyticsModule.setEnableStorage(!!value);
    }

    showWallet(): void {
        this.setOptions({hideWallet: false});
    }

    hideWallet(): void {
        this.setOptions({hideWallet: true});
    }

    // --- Merchant variables
    hasVariable(key: string): boolean {
        return merchantVariableModule.has(key);
    }

    getVariable(key: string) {
        return merchantVariableModule.get(key);
    }

    setVariable(key: string, value: any, scope = StorageScope.MEMORY): void {
        merchantVariableModule.set(key, value, scope)
    }

    deleteVariable(key: string): void {
        merchantVariableModule.delete(key);
    }

    evaluatePanelOptions(aliasOrId: string | number): PanelOptions {
        return evaluatePanelOptions({panel: aliasOrId});
    }

    allVariables(): Record<string, any> {
        return merchantVariableModule.all();
    }

    // --- Merchant functions
    hasFunction(key: string): boolean {
        return !!merchantFunctionModule.get(key);
    }

    getFunction(key: string): MerchantFunction | undefined {
        return merchantFunctionModule.get(key);
    }

    addFunction(key: string, func: MerchantFunction): void {
        merchantFunctionModule.add({[key]: func});
    }

    addFunctions(functions: Record<string, MerchantFunction>): void {
        merchantFunctionModule.add(functions);
    }

    callFunction(key: string, ...args: any[]): [boolean, any] {
        return merchantFunctionModule.call(key, ...args);
    }

    allFunctions(): Record<string, Function> {
        return merchantFunctionModule.all();
    }

    getExceptionHandler(): (exception: Error, key?: string, code?: string) => void {
        return merchantFunctionModule.exceptionHandler;
    }

    setExceptionHandler(exceptionHandler: (exception: Error, key?: string, code?: string) => void) {
        const oldExceptionHandler = merchantFunctionModule.exceptionHandler;
        merchantFunctionModule.exceptionHandler = exceptionHandler;
        return oldExceptionHandler;
    }

    // --- Analytics
    sendAnalyticsEvent(payload: Record<string, any>): void {
        dispatchAnalyticsEvent(AnalyticsEventType.CUSTOM, payload);
    }

    // Both analytics dispatchers hand their listeners exactly one event (AnalyticsModule.init)
    addAnalyticsEventListener(type: string, callback: (event: AnalyticsEvent) => void) {
        const safeCallback = (...args: [AnalyticsEvent]) => {
            try {
                callback(...args);
            } catch (e) {
                console.error(e);
            }
        };
        if (type === "*") {
            return analyticsModule.allEventsDispatcher.addListener(safeCallback);
        }

        return analyticsModule.eventsByTypeDispatcher.addListener(type, safeCallback);
    }

    getSplitJourneys() {
        // TODO use from somewhere else
        return analyticsModule.client?.environment?.journeys;
    }

    // --- Build version
    getBuildVersion() {
        return {
            sdkVersion,
            widgetVersion,
        };
    }

    printBuildVersion(): void {
        const buildVersion = this.getBuildVersion();
        const printInfo = (label: string, version: Record<string, any>) => {
            console.group(label);
            console.log("build: ", version.build);
            console.log("time: ", version.time);
            console.log("age: ", formatAgoMessage(version.time));
            console.groupEnd();
        };
        printInfo("Blink SDK", buildVersion.sdkVersion);
        printInfo("Iframe", buildVersion.widgetVersion);
    }

    // Journeys
    runAction(blinkAction: string | null): void {
        runBlinkAction(blinkAction);
    }

    markURLVisited(url: string, metadata?: Record<string, any>): void {
        markURLVisited(url, metadata);
    }

    // A function for testing journey options (usually meant to be called in the
    // console or in automated tests, not in production).
    // Returns an object containing an auto-generated ID and alias for the mock
    // journey, and a cleanup function that can be called to close the execution
    // loop of the journey.
    testJourneyOptions(options: JourneyOptions) {
        const id = "test_journey_id_" + generateUniqueId().replace(/-/g, "_");
        const alias = "test_journey_alias_" + generateUniqueId().replace(/-/g, "_");
        const journey = {id, alias, options};
        const evaluator = evaluateConditionAndRun(journey);
        return {
            id,
            alias,
            cleanup: () => evaluator?.cleanup(),
        };
    }

    // TODO @branch Add Helper for donation data
    getUserMetadata(key: string | null = null) {
        const {metadata} = this.getUserData();
        return (key != null) ? metadata[key] : metadata;
    }

    async setUserMetadata(metadata: Record<string, any>): Promise<void> {
        walletModule.widget.sendMessage(IFrameMessages.EDIT_MERCHANT_USER_METADATA, {
            metadata,
            merchantId: getMerchantId(),
        });
    }

    async attemptAuth(options: { emailAddress: string }): Promise<void> {
        const {emailAddress} = options;
        // TODO Return the user if authenticated, otherwise fail
        // TODO should prompt 6 digit code or password if required
    }

    // --- Deprecated API
    setEnableAnalyticsStorage(value: boolean): void {
        this.setAnalyticsStorageEnabled(value);
    }

    promptSubscriptionPopup(options: string | PanelPromptOptions): void {
        this.promptSubscription(options);
    }

    promptGiftSubscriptionPopup(options: string | PanelPromptOptions): void {
        this.promptGiftSubscription(options)
    }

    promptDonationPopup(options: string | PanelPromptOptions): void {
        this.promptDonation(options);
    }

    onSubscriptionStatusChange(callback: (data: { subscribed: boolean }) => void): void {
        this.onSubscriptionChange((subscription) => callback({subscribed: !!subscription}));
    }
}

// The SDK publishes itself here for the merchant page to reach
declare global {
    interface Window {
        blink?: BlinkSDK;
    }
}

const blinkSDK = new BlinkSDK();

if (self.blink == null) {
    self.blink = blinkSDK;
}

export default blinkSDK;
